import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
  PLATFORM_ID,
  DestroyRef,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideAngularModule, Share2, X, Printer, Presentation, Loader2, FileSpreadsheet } from 'lucide-angular';
import { LessonPlanService } from '../../services/lesson-plan.service';
import {
  PricingModalComponent,
  PaywallTarget,
} from '../../../../paywall/pricing-modal/pricing-modal';
import { SubscriptionSuccessModalComponent } from '../../../../paywall/subscription-success-modal/subscription-success-modal';
import { Product } from '../../../../paywall/billing-api.service';
import { AuthService } from '../../../../../common/auth/auth.service';

type PendingExportAction = 'pdf' | 'pptx' | 'googleSlides' | 'googleDocs' | null;

@Component({
  selector: 'app-export-modal',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, PricingModalComponent, SubscriptionSuccessModalComponent],
  templateUrl: './export-modal.html',
  styleUrl: './export-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportModalComponent {
  private readonly lessonPlanService = inject(LessonPlanService);
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  private readonly isExportingPdf$$ = new BehaviorSubject<boolean>(false);
  protected readonly isExportingPdf$ = this.isExportingPdf$$.asObservable();

  private readonly isExportingPptx$$ = new BehaviorSubject<boolean>(false);
  protected readonly isExportingPptx$ = this.isExportingPptx$$.asObservable();

  private readonly isExportingGoogleDocs$$ = new BehaviorSubject<boolean>(false);
  protected readonly isExportingGoogleDocs$ = this.isExportingGoogleDocs$$.asObservable();

  protected readonly googleSlides$ = this.lessonPlanService.googleSlides$;

  // Pricing modal state
  protected readonly isPricingModalOpen = signal<boolean>(false);
  protected readonly paywallTarget = signal<PaywallTarget>('export_pdf');
  private pendingExportAction: PendingExportAction = null;

  // Subscription success modal state
  protected readonly isSubscriptionSuccessOpen = signal<boolean>(false);
  protected readonly subscribedProductName = signal<string>('Premium');

  @Input()
  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
    // Ensure user profile is loaded when modal opens
    if (value) {
      this.authService.getProfile().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  @Output() readonly closed = new EventEmitter<void>();

  protected readonly icons = {
    Share2,
    X,
    Printer,
    Presentation,
    Loader2,
    FileSpreadsheet,
  };

  protected close(): void {
    this.closed.emit();
  }

  /** Check if user has an active premium subscription */
  private hasPremium(): boolean {
    return this.authService.hasPremium();
  }

  // Pricing modal handlers
  protected openPricingModal(action: PendingExportAction): void {
    this.pendingExportAction = action;
    this.paywallTarget.set(this.actionToPaywallTarget(action));
    this.isPricingModalOpen.set(true);
  }

  private actionToPaywallTarget(action: PendingExportAction): PaywallTarget {
    switch (action) {
      case 'pdf':
        return 'export_pdf';
      case 'pptx':
        return 'export_pptx';
      case 'googleSlides':
        return 'export_google_slides';
      case 'googleDocs':
        return 'export_google_docs';
      default:
        return 'export_pdf';
    }
  }

  protected closePricingModal(): void {
    this.isPricingModalOpen.set(false);
    this.pendingExportAction = null;
  }

  protected onPricingProductSelected(): void {
    // Don't close the pricing modal - let it handle showing the checkout modal internally
    // The pricing modal will close itself after successful payment
  }

  protected onPaymentSuccess(product: Product): void {
    this.isPricingModalOpen.set(false);
    this.subscribedProductName.set(product.name);
    this.isSubscriptionSuccessOpen.set(true);

    // Note: Profile refresh is handled by the subscription success modal's polling logic
    // Execute the pending export action after successful subscription
    this.executePendingAction();
  }

  protected closeSubscriptionSuccess(): void {
    this.isSubscriptionSuccessOpen.set(false);
    this.close();
  }

  private executePendingAction(): void {
    if (!this.pendingExportAction) return;

    switch (this.pendingExportAction) {
      case 'pdf':
        this.executePdfExport();
        break;
      case 'googleSlides':
        this.executeGoogleSlidesExport();
        break;
      case 'pptx':
        this.executePptxExport();
        break;
      case 'googleDocs':
        this.executeGoogleDocsExport();
        break;
    }
    this.pendingExportAction = null;
  }

  // Export handlers - check premium status first, show pricing modal only if not premium
  protected handlePdfExport(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.close();
      return;
    }
    // If user has premium, export directly without paywall
    if (this.hasPremium()) {
      this.executePdfExport();
    } else {
      this.openPricingModal('pdf');
    }
  }

  protected handleGoogleSlides(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.close();
      return;
    }
    // If user has premium, export directly without paywall
    if (this.hasPremium()) {
      this.executeGoogleSlidesExport();
    } else {
      this.openPricingModal('googleSlides');
    }
  }

  protected handlePPTX(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.close();
      return;
    }
    // If user has premium, export directly without paywall
    if (this.hasPremium()) {
      this.executePptxExport();
    } else {
      this.openPricingModal('pptx');
    }
  }

  protected handleGoogleDocs(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.close();
      return;
    }
    // If user has premium, export directly without paywall
    if (this.hasPremium()) {
      this.executeGoogleDocsExport();
    } else {
      this.openPricingModal('googleDocs');
    }
  }

  // Actual export methods - to be called after successful subscription
  private executePdfExport(): void {
    this.isExportingPdf$$.next(true);

    this.lessonPlanService
      .exportToPdf()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isExportingPdf$$.next(false);
          this.close();
        },
        error: () => {
          this.isExportingPdf$$.next(false);
        },
      });
  }

  private executeGoogleSlidesExport(): void {
    const googleSlides = this.lessonPlanService.googleSlides;
    if (googleSlides?.presentationUrl) {
      // Track the export and open the presentation
      this.lessonPlanService
        .trackGoogleSlidesExport()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          complete: () => {
            // Tracking completed (or failed silently)
          },
        });
      window.open(googleSlides.presentationUrl, '_blank');
    }
    this.close();
  }

  private executePptxExport(): void {
    this.isExportingPptx$$.next(true);

    this.lessonPlanService
      .exportToPptx()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isExportingPptx$$.next(false);
          this.close();
        },
        error: () => {
          this.isExportingPptx$$.next(false);
        },
      });
  }

  private executeGoogleDocsExport(): void {
    this.isExportingGoogleDocs$$.next(true);

    this.lessonPlanService
      .exportToGoogleDocs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isExportingGoogleDocs$$.next(false);
          this.close();
        },
        error: () => {
          this.isExportingGoogleDocs$$.next(false);
        },
      });
  }
}
